import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Loader2, PhoneOff, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import {
  adminNavApi, ADMIN_NAV_KEYS, downloadAdminAttachment,
  type AdminNavAttachmentRow, type AdminNavExceptionItem, type AdminNavFormRow,
  type AdminNavLoad, type AdminNavTrackRow,
} from '@/features/navigator/adminNavigation.api';
import { navError, type FormLedgerStatus } from '@/features/navigator/navigator.api';
import {
  ATTESTATION_STATUS_KEYS, CHECKLIST_STEP_KEYS, FORM_STATUS_KEYS,
  PREAUTH_STATUS_KEYS, fmtNy, serviceLabel,
} from '@/pages/navigator/labels';
import { useAuthStore } from '@/stores/auth.store';
import { toast } from '@/stores/toast.store';
import { useLang, useT, type StringKey } from '@/i18n';
import { cn } from '@/lib/utils';
import { Pager as ChassisPager } from '@/components/chassis/Pager';
import { EmptyState } from '@/components/chassis/States';

const POLL_MS = 30_000;

// Paginación 25 en 25 (CEO 2026-08-26): la lista de excepciones no tiene tope
// estructural — con el pool creciendo, la landing del admin no puede pintar
// cientos de filas. El encabezado dice el total real.
const PAGE_SIZE = 25;

const FORM_TYPE_KEYS: Record<string, StringKey> = {
  hrsn_screener: 'navigator.docs.kind.hrsn_screener',
  eligibility_assessment: 'navigator.docs.kind.eligibility_assessment',
  outreach_tracker: 'navigator.docs.kind.outreach_tracker',
  navigation_reimbursement: 'navigator.docs.kind.navigation_reimbursement',
};

/**
 * F5-R4a — el filtro «Ver» de cada excepción (dibujo: «abre la lista ya
 * filtrada, no una pantalla genérica»). Filtra client-side sobre los items
 * VIVOS del endpoint de excepciones — cero endpoints nuevos.
 */
type ExcType = 'unreimbursed' | 'pendingForms' | 'overdue' | 'timer' | 'stale' | 'units' | 'overrides';

const EXC_PREDICATE: Record<ExcType, (i: AdminNavExceptionItem) => boolean> = {
  unreimbursed: (i) => i.unreimbursedSessions > 0,
  pendingForms: (i) => i.pendingForms > 0,
  overdue: (i) => i.overdueFollowups > 0,
  timer: (i) => i.timerDiscrepancies > 0,
  stale: (i) => i.staleAppointments > 0,
  units: (i) => i.unitsExhausted,
  overrides: (i) => i.overridden,
};

/**
 * F5-R4a — Excepciones según el dibujo del CEO: primero el dinero en riesgo
 * (dos tarjetas ámbar con su acción), después las excepciones operativas en
 * lista con SU REGLA y SU DUEÑO, y abajo los casos con la maquinaria admin de
 * siempre. La franja de stats y el banner anterior quedan superseded por esta
 * estructura (mismos conteos vivos, presentados como el dibujo manda).
 */
export function NavExceptionsDashboard(): JSX.Element {
  const t = useT();
  const [page, setPage] = useState(0);
  const [typeFilter, setTypeFilter] = useState<ExcType | null>(null);
  const exceptionsQ = useQuery({
    queryKey: ADMIN_NAV_KEYS.exceptions,
    queryFn: () => adminNavApi.exceptions(),
    refetchInterval: POLL_MS,
  });
  const loadsQ = useQuery({
    queryKey: ADMIN_NAV_KEYS.loads,
    queryFn: () => adminNavApi.assignmentLoads(),
    refetchInterval: POLL_MS,
  });
  // Preautorizaciones esperando firma: el server YA agrega byPreauth en el
  // summary (Wave 11) — pendiente + en_revision es exactamente «sin firmar».
  const summaryQ = useQuery({
    queryKey: ADMIN_NAV_KEYS.summary,
    queryFn: () => adminNavApi.summary(),
    refetchInterval: POLL_MS,
  });

  if (exceptionsQ.isLoading) return <Loading />;
  if (exceptionsQ.isError || !exceptionsQ.data) return <LoadError />;

  const { counts, items: allItems } = exceptionsQ.data;
  const loads = loadsQ.data ?? [];
  const preauthPending = (summaryQ.data?.byPreauth?.pendiente ?? 0)
    + (summaryQ.data?.byPreauth?.en_revision ?? 0);

  const opsRows: Array<{ type: ExcType; n: number; label: string; rule: string; owner: string }> = [
    { type: 'overdue', n: counts.overdueFollowups, label: t('f5r4a.exc.t.overdue'), rule: t('f5r4a.exc.r.overdue'), owner: t('f5r4a.exc.owner.navigator') },
    { type: 'pendingForms', n: counts.pendingForms, label: t('f5r4a.exc.t.pendingForms'), rule: t('f5r4a.exc.r.pendingForms'), owner: t('f5r4a.exc.owner.navigator') },
    { type: 'stale', n: counts.staleAppointments, label: t('f5r4a.exc.t.stale'), rule: t('f5r4a.exc.r.stale'), owner: t('f5r4a.exc.owner.navigator') },
    { type: 'timer', n: counts.timerDiscrepancies, label: t('f5r4a.exc.t.timer'), rule: t('f5r4a.exc.r.timer'), owner: t('f5r4a.exc.owner.supervisor') },
    { type: 'units', n: counts.unitsExhausted, label: t('f5r4a.exc.t.units'), rule: t('f5r4a.exc.r.units'), owner: t('f5r4a.exc.owner.admin') },
    { type: 'overrides', n: counts.overrides, label: t('f5r4a.exc.t.overrides'), rule: t('f5r4a.exc.r.overrides'), owner: t('f5r4a.exc.owner.admin') },
  ];

  const allClear = allItems.length === 0
    && counts.unreimbursedSessions === 0
    && preauthPending === 0
    && opsRows.every((r) => r.n === 0);

  // «items» ES la lista visible (filtrada por el «Ver» activo) — el nombre se
  // conserva a propósito: la custodia de paginación ancla el slice literal.
  const items = typeFilter ? allItems.filter(EXC_PREDICATE[typeFilter]) : allItems;
  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const current = Math.min(page, pages - 1);
  const visible = items.slice(current * PAGE_SIZE, (current + 1) * PAGE_SIZE);
  const filterLabel = typeFilter === 'unreimbursed'
    ? t('f5r4a.exc.m1.label')
    : opsRows.find((r) => r.type === typeFilter)?.label;

  const pickFilter = (next: ExcType): void => {
    setTypeFilter((cur) => (cur === next ? null : next));
    setPage(0);
  };

  return (
    <div className="flex flex-col gap-5 font-body text-nav-navy">
      {allClear ? (
        /* Estado propio del dibujo: «Nada en riesgo ahora mismo» — salvia,
           sin botón: es la buena noticia. */
        <div className="rounded-xl border-[1.5px] border-nav-card bg-white">
          <EmptyState
            icon={<ShieldCheck className="h-6 w-6" aria-hidden />}
            title={t('f5r4a.exc.emptyTitle')}
            body={t('f5r4a.exc.emptyBody')}
          />
        </div>
      ) : (
        <>
          <div>
            <p className="font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-amberText mb-3.5">
              {t('f5r4a.exc.moneyKicker')}
            </p>
            <div className="grid gap-3.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
              <div className="rounded-xl border-[1.5px] border-nav-amber bg-nav-amberTint p-5 text-nav-amberText">
                <div className="flex items-start gap-3.5">
                  <span className="flex-none font-head text-[34px] font-extrabold leading-none tracking-[-0.02em] tabular-nums">
                    {counts.unreimbursedSessions}
                  </span>
                  <span className="flex-1 min-w-0">
                    {/* El label hereda el ámbar de la tarjeta (dibujo: sin color propio). */}
                    <span className="block font-head text-[14.5px] font-bold tracking-[-0.01em]">
                      {t('f5r4a.exc.m1.label')}
                    </span>
                    <span className="block mt-[5px] text-[12.5px] leading-relaxed">{t('f5r4a.exc.m1.rule')}</span>
                  </span>
                </div>
                <div className="flex items-center gap-2.5 mt-4 flex-wrap">
                  <button
                    type="button"
                    onClick={() => pickFilter('unreimbursed')}
                    aria-pressed={typeFilter === 'unreimbursed'}
                    className="inline-flex items-center min-h-[44px] px-4 rounded-lg border-[1.5px] border-nav-amber bg-white font-head text-[13px] font-bold text-nav-amberText hover:bg-nav-amberTint transition-colors duration-150 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
                  >
                    {t('f5r4a.exc.m1.action', { n: counts.unreimbursedSessions })}
                  </button>
                  {/* NECESITA-SERVER: «La más vieja: hace N días». */}
                  <span className="text-[11px] opacity-80">{t('f5r4a.exc.oldestNa')}</span>
                </div>
              </div>

              <div className="rounded-xl border-[1.5px] border-nav-amber bg-nav-amberTint p-5 text-nav-amberText">
                <div className="flex items-start gap-3.5">
                  <span className="flex-none font-head text-[34px] font-extrabold leading-none tracking-[-0.02em] tabular-nums">
                    {summaryQ.data ? preauthPending : '—'}
                  </span>
                  <span className="flex-1 min-w-0">
                    {/* El label hereda el ámbar de la tarjeta (dibujo: sin color propio). */}
                    <span className="block font-head text-[14.5px] font-bold tracking-[-0.01em]">
                      {t('f5r4a.exc.m2.label')}
                    </span>
                    <span className="block mt-[5px] text-[12.5px] leading-relaxed">{t('f5r4a.exc.m2.rule')}</span>
                  </span>
                </div>
                <div className="flex items-center gap-2.5 mt-4 flex-wrap">
                  <Link
                    to="/admin/preauthorizations"
                    className="inline-flex items-center min-h-[44px] px-4 rounded-lg border-[1.5px] border-nav-amber bg-white font-head text-[13px] font-bold text-nav-amberText hover:bg-nav-amberTint transition-colors duration-150 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
                  >
                    {t('f5r4a.exc.m2.action')}
                  </Link>
                  <span className="text-[11px] opacity-80">{t('f5r4a.exc.oldestNa')}</span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <p className="font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-teal mb-3.5">
              {t('f5r4a.exc.opsKicker')}
            </p>
            <div className="rounded-xl border-[1.5px] border-nav-card bg-white overflow-hidden">
              {opsRows.map((r, i) => (
                <div
                  key={r.type}
                  className={cn(
                    'flex items-center gap-3.5 flex-wrap min-h-[68px] px-5 py-3',
                    i > 0 && 'border-t border-nav-hair',
                  )}
                >
                  <span className="flex-none inline-flex items-center justify-center min-w-[44px] min-h-[44px] rounded-lg bg-nav-hair border border-nav-card font-head text-[15px] font-extrabold tabular-nums text-nav-navy">
                    {r.n}
                  </span>
                  <span className="flex-1 min-w-[180px]">
                    <span className="block font-head text-[13.5px] font-bold text-nav-navy">{r.label}</span>
                    <span className="block mt-0.5 text-[12.5px] leading-normal text-nav-slate">{r.rule}</span>
                  </span>
                  <span className="flex-none rounded-full px-[11px] py-1 bg-nav-tealTint text-nav-tealDark border border-[#c9e2e2] font-head text-[11px] font-bold whitespace-nowrap">
                    {r.owner}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="min-h-[44px]"
                    aria-label={t('f5r4a.exc.verAria', { tipo: r.label })}
                    aria-pressed={typeFilter === r.type}
                    onClick={() => pickFilter(r.type)}
                  >
                    {t('f5r4a.exc.ver')}
                  </Button>
                </div>
              ))}
            </div>
            {/* NECESITA-SERVER: los tipos dibujados que el endpoint aún no cuenta. */}
            <p className="text-[11px] text-nav-slate mt-2">{t('f5r4a.exc.missingTypes')}</p>
          </div>

          <p className="rounded-[10px] border-[1.5px] border-nav-card bg-white px-4 py-3.5 text-[12.5px] leading-relaxed text-nav-slate">
            {t('f5r4a.exc.note')}
          </p>

          <section className="rounded-xl border-[1.5px] border-nav-card bg-white">
            <div className="px-4 py-3 border-b border-nav-hair flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h2 className="font-head text-[13px] font-bold text-nav-navy">
                  {t('f5r4a.exc.caseList')} ({items.length})
                </h2>
                <p className="text-[11px] text-nav-slate mt-0.5">{t('admin.nav.exceptionsHint')}</p>
              </div>
              {typeFilter && filterLabel && (
                <button
                  type="button"
                  onClick={() => { setTypeFilter(null); setPage(0); }}
                  className="inline-flex items-center gap-1.5 min-h-[44px] px-3.5 rounded-full border-[1.5px] border-nav-teal bg-nav-tealTint font-head text-[12px] font-bold text-nav-tealDark hover:bg-white transition-colors duration-150 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
                  aria-label={t('f5r4a.exc.filterClear')}
                >
                  {t('f5r4a.exc.filterChip', { tipo: filterLabel })} ×
                </button>
              )}
            </div>
            {items.length === 0 ? (
              <p className="text-sm text-nav-slate px-4 py-6">{t('admin.nav.emptyExceptions')}</p>
            ) : (
              <ul className="divide-y divide-nav-hair">
                {visible.map((row) => (
                  <ExceptionRow key={row.caseId} row={row} loads={loads} />
                ))}
              </ul>
            )}
            {items.length > PAGE_SIZE && (
              <div className="px-4 pb-3">
                <ChassisPager
                  page={current}
                  pages={pages}
                  onPage={setPage}
                  prevLabel={t('adminNav.pager.prev')}
                  nextLabel={t('adminNav.pager.next')}
                  pageLabel={t('adminNav.pager.page', { page: current + 1, pages })}
                />
              </div>
            )}
          </section>
        </>
      )}

      {/* Conserva (E-23): la tabla de cargas de asignación — viva, y el dibujo
          ni la dibuja ni la mata. */}
      <LoadsTable loads={loads} loading={loadsQ.isLoading} />
    </div>
  );
}

function LoadsTable({ loads, loading }: { loads: AdminNavLoad[]; loading: boolean }): JSX.Element {
  const t = useT();
  return (
    <section className="overflow-x-auto rounded-xl border-[1.5px] border-nav-card bg-white">
      <div className="px-4 py-3 border-b border-nav-hair">
        <h2 className="font-head text-[13px] font-bold text-nav-navy">{t('admin.nav.loads.title')}</h2>
      </div>
      {loading && loads.length === 0 ? (
        <Loading />
      ) : loads.length === 0 ? (
        <p className="text-sm text-nav-slate px-4 py-4">{t('adminNav.noNavigators')}</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-nav-softer text-nav-slate">
            <tr>
              <th className="px-3 h-[38px] text-left font-head text-[10.5px] font-bold tracking-[0.08em] uppercase whitespace-nowrap">{t('adminNav.col.navigator')}</th>
              <th className="px-3 h-[38px] text-right font-head text-[10.5px] font-bold tracking-[0.08em] uppercase whitespace-nowrap">{t('admin.nav.loads.openEpisodes')}</th>
              <th className="px-3 h-[38px] text-right font-head text-[10.5px] font-bold tracking-[0.08em] uppercase whitespace-nowrap">{t('admin.nav.loads.overdueFollowups')}</th>
              <th className="px-3 h-[38px] text-right font-head text-[10.5px] font-bold tracking-[0.08em] uppercase whitespace-nowrap">{t('admin.nav.loads.upcoming')}</th>
              <th className="px-3 h-[38px] text-right font-head text-[10.5px] font-bold tracking-[0.08em] uppercase whitespace-nowrap">{t('admin.nav.loads.score')}</th>
            </tr>
          </thead>
          <tbody>
            {loads.map((r) => (
              <tr key={r.navigatorId} className="border-t border-nav-hair">
                <td className="px-3 py-2 text-nav-navy">{r.navigatorName}</td>
                <td className="px-3 py-2 text-right text-nav-navy">{r.openEpisodes}</td>
                <td className="px-3 py-2 text-right text-nav-navy">{r.overdueFollowups}</td>
                <td className="px-3 py-2 text-right text-nav-navy">{r.upcomingAppointments}</td>
                <td className="px-3 py-2 text-right font-medium text-nav-navy">{r.score}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/**
 * F5-R4a: exportado — la fila con toda la maquinaria admin del caso la reusan
 * el drill-down de «Por navigator» y esta landing. Sin cambios de lógica.
 */
export function ExceptionRow({ row, loads }: { row: AdminNavExceptionItem; loads: AdminNavLoad[] }): JSX.Element {
  const t = useT();
  const [open, setOpen] = useState(false);
  const flags: string[] = [];
  if (row.unreimbursedSessions > 0) flags.push(t('admin.nav.flag.unreimbursed', { n: row.unreimbursedSessions }));
  if (row.pendingForms > 0) flags.push(t('admin.nav.flag.pendingForms', { n: row.pendingForms }));
  if (row.overdueFollowups > 0) flags.push(t('admin.nav.flag.overdue', { n: row.overdueFollowups }));
  if (row.timerDiscrepancies > 0) flags.push(t('admin.nav.flag.timer', { n: row.timerDiscrepancies }));
  if (row.staleAppointments > 0) flags.push(t('admin.nav.flag.stale', { n: row.staleAppointments }));
  if (row.unitsExhausted) flags.push(t('admin.nav.flag.units'));
  if (row.overridden) flags.push(t('admin.nav.flag.override'));
  if (row.outreachExhausted) flags.push(t('admin.nav.flag.parked'));

  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-nav-navy">
            <span className="font-medium">{row.navigatorName ?? t('admin.nav.unassigned')}</span>
            <span className="font-mono text-xs text-nav-slate ml-2">#{row.caseId}</span>
          </p>
          <p className="text-[11px] text-nav-slate mt-1 flex flex-wrap gap-1.5">
            {flags.map((f) => (
              <span key={f} className="rounded-full bg-nav-amberTint text-nav-amberText px-2 py-0.5">{f}</span>
            ))}
          </p>
        </div>
        {/* El link "Abrir caso" que vivía acá se quitó: apuntaba a
            /navigator/cases/:id, que es ruta y backend exclusivos del rol
            navigator — para el admin rebotaba a /admin sin abrir nada. El
            detalle que el admin necesita (tracks, adjuntos, forms) se muestra
            abajo, en su propia superficie. */}
        <div className="flex gap-1.5 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => setOpen((v) => !v)}>
            {open ? t('admin.nav.hideDetails') : t('admin.nav.showDetails')}
          </Button>
        </div>
      </div>
      {open && (
        <div className="mt-3 flex flex-col gap-3">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <CaseForms caseId={row.caseId} />
            <ReassignForm caseId={row.caseId} currentNavigatorId={row.navigatorId} loads={loads} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <TracksPanel caseId={row.caseId} />
            <AttachmentsPanel caseId={row.caseId} />
          </div>
          <AdminActionsPanel row={row} />
        </div>
      )}
    </li>
  );
}

/**
 * NAV-B-11 — las dos palancas admin que el backend exponía sin botón:
 * desbloquear el outreach de un caso parkeado y dispensar un paso del checklist
 * con motivo. El waive queda en el registro (nota `[waived by <rol>]` + audit);
 * desde PD-1 el checklist ya no gatea el cierre, así que dispensar es una
 * corrección del expediente, no una llave.
 */
function AdminActionsPanel({ row }: { row: AdminNavExceptionItem }): JSX.Element {
  const t = useT();
  const qc = useQueryClient();
  const [step, setStep] = useState('');
  const [reason, setReason] = useState('');

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['admin', 'navigation'] });
  };

  const unblockMut = useMutation({
    mutationFn: () => adminNavApi.unblockOutreach(row.caseId),
    onSuccess: () => { toast.success(t('admin.nav.unblockDone')); invalidate(); },
    // El backend contesta tipado (NOT_EXHAUSTED / CASE_CLOSED): se muestra tal
    // cual — si el caso no estaba parkeado, eso es exactamente lo que hay que saber.
    onError: (err) => toast.error(t('admin.nav.unblockFailed'), navError(err).message ?? ''),
  });

  const waiveMut = useMutation({
    mutationFn: () => adminNavApi.waiveStep(row.caseId, Number(step), reason.trim()),
    onSuccess: () => {
      toast.success(t('admin.nav.waiveDone'));
      setStep('');
      setReason('');
      invalidate();
    },
    onError: (err) => toast.error(t('admin.nav.waiveFailed'), navError(err).message ?? ''),
  });

  return (
    <div className="rounded-[10px] border-[1.5px] border-nav-card bg-white p-3">
      <h3 className="font-head text-[12px] font-bold text-nav-navy mb-2">{t('admin.nav.actions.title')}</h3>
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[11px] text-nav-slate min-w-0">
            {row.outreachExhausted ? t('admin.nav.flag.parked') : t('admin.nav.unblockHint')}
          </p>
          <Button
            variant={row.outreachExhausted ? 'primary' : 'secondary'}
            size="sm"
            disabled={unblockMut.isPending}
            onClick={() => unblockMut.mutate()}
          >
            {unblockMut.isPending
              ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              : <PhoneOff className="h-4 w-4" aria-hidden />}
            {t('admin.nav.unblock')}
          </Button>
        </div>

        <div className="border-t border-nav-card pt-3">
          <p className="text-xs font-medium text-nav-navy">{t('admin.nav.waiveTitle')}</p>
          <p className="text-[11px] text-nav-slate mt-0.5 mb-2">{t('admin.nav.waiveHint')}</p>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label htmlFor={`waive-step-${row.caseId}`}>{t('admin.nav.waiveStep')}</Label>
              <Select
                id={`waive-step-${row.caseId}`}
                value={step}
                className="max-w-[16rem]"
                onChange={(e) => setStep(e.target.value)}
              >
                <option value="">{t('navigator.docs.choose')}</option>
                {Object.entries(CHECKLIST_STEP_KEYS).map(([n, key]) => (
                  <option key={n} value={n}>{n}. {t(key)}</option>
                ))}
              </Select>
            </div>
            <div className="flex-1 min-w-[14rem]">
              <Label htmlFor={`waive-reason-${row.caseId}`}>{t('admin.nav.waiveReason')}</Label>
              <Textarea
                id={`waive-reason-${row.caseId}`}
                value={reason}
                maxLength={500}
                rows={1}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
            <Button
              variant="secondary"
              size="sm"
              disabled={!step || reason.trim().length < 3 || waiveMut.isPending}
              onClick={() => waiveMut.mutate()}
            >
              {waiveMut.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
              {t('admin.nav.waiveApply')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * NAV-B-11 + NAV-A-01 — los tracks del caso, con la firma de la preautorización.
 *
 * La firma es de esta superficie por diseño (manual §VIII): el navigator recibe
 * PREAUTH_SIGNOFF_NOT_YOURS por su propia ruta. Denegar exige el motivo que
 * mandó SOMOS — el servidor lo rechaza sin él (DENIAL_REASON_REQUIRED), así que
 * el botón no se habilita hasta que esté escrito.
 *
 * F5-R4a: exportado — lo reusa el «Abrir» del listado de Casos.
 */
export function TracksPanel({ caseId }: { caseId: string }): JSX.Element {
  const t = useT();
  const lang = useLang();
  const qc = useQueryClient();
  const [denyFor, setDenyFor] = useState<string | null>(null);
  const [denialReason, setDenialReason] = useState('');

  const q = useQuery({
    queryKey: ADMIN_NAV_KEYS.tracks(caseId),
    queryFn: () => adminNavApi.caseTracks(caseId),
  });

  const signMut = useMutation({
    mutationFn: (v: { trackId: string; status: 'autorizada' | 'denegada'; reason?: string }) =>
      adminNavApi.setPreauthStatus(v.trackId, v.status, v.reason),
    onSuccess: () => {
      toast.success(t('admin.nav.preauthDone'));
      setDenyFor(null);
      setDenialReason('');
      void qc.invalidateQueries({ queryKey: ADMIN_NAV_KEYS.tracks(caseId) });
      void qc.invalidateQueries({ queryKey: ADMIN_NAV_KEYS.exceptions });
    },
    onError: (err) => toast.error(t('admin.nav.preauthFailed'), navError(err).message ?? ''),
  });

  if (q.isLoading) return <Loading />;
  if (q.isError) return <LoadError />;
  const rows: AdminNavTrackRow[] = q.data ?? [];

  return (
    <div className="rounded-[10px] border-[1.5px] border-nav-card bg-white p-3">
      <h3 className="font-head text-[12px] font-bold text-nav-navy mb-2">{t('admin.nav.tracksTitle')}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-nav-slate">{t('admin.nav.tracksEmpty')}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((tr) => {
            const signable = tr.preauthRequired
              && !tr.cancelledAt
              && (tr.preauthStatus === 'pendiente' || tr.preauthStatus === 'en_revision');
            return (
              <li key={tr.id} className="rounded-lg border border-nav-hair bg-nav-softer px-2.5 py-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm text-nav-navy font-medium">
                    {serviceLabel(tr.serviceKey, lang)}
                    {tr.cancelledAt && (
                      <span className="ml-1.5 text-[11px] text-nav-slate">
                        {t('navigator.workspace.notReferred')}
                      </span>
                    )}
                  </p>
                  {signable && (
                    <span className="flex gap-1.5">
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={signMut.isPending}
                        onClick={() => signMut.mutate({ trackId: tr.id, status: 'autorizada' })}
                      >
                        {t('admin.nav.preauthAuthorize')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={signMut.isPending}
                        onClick={() => setDenyFor(denyFor === tr.id ? null : tr.id)}
                      >
                        {t('admin.nav.preauthDeny')}
                      </Button>
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-nav-slate mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span>
                    {t('navigator.docs.attestation')}: {ATTESTATION_STATUS_KEYS[tr.attestationStatus]
                      ? t(ATTESTATION_STATUS_KEYS[tr.attestationStatus])
                      : tr.attestationStatus}
                  </span>
                  <span>
                    {t('navigator.docs.preauth')}: {PREAUTH_STATUS_KEYS[tr.preauthStatus]
                      ? t(PREAUTH_STATUS_KEYS[tr.preauthStatus])
                      : tr.preauthStatus}
                  </span>
                  {tr.preauthResubmitCount > 0 && (
                    <span>{t('navigator.docs.resubmitCount', { n: tr.preauthResubmitCount })}</span>
                  )}
                </p>
                {tr.preauthStatus === 'denegada' && tr.preauthDenialReason && (
                  <p className="text-[11px] text-nav-red mt-1">{tr.preauthDenialReason}</p>
                )}
                {denyFor === tr.id && (
                  <div className="mt-2 flex flex-wrap items-end gap-2">
                    <div className="flex-1 min-w-[12rem]">
                      <Label htmlFor={`deny-reason-${tr.id}`}>{t('admin.nav.preauthDenyReason')}</Label>
                      <Textarea
                        id={`deny-reason-${tr.id}`}
                        value={denialReason}
                        maxLength={500}
                        rows={2}
                        placeholder={t('navigator.docs.reasonPlaceholder')}
                        onChange={(e) => setDenialReason(e.target.value)}
                      />
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={denialReason.trim().length < 3 || signMut.isPending}
                      onClick={() => signMut.mutate({
                        trackId: tr.id, status: 'denegada', reason: denialReason.trim(),
                      })}
                    >
                      {signMut.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
                      {t('admin.nav.preauthDeny')}
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * NAV-B-11 — ver (y bajar) lo que se va a firmar. La descarga queda auditada.
 * F5-R4a: exportado — lo reusa el «Abrir» del listado de Casos.
 */
export function AttachmentsPanel({ caseId }: { caseId: string }): JSX.Element {
  const t = useT();
  const accessToken = useAuthStore((s) => s.accessToken);
  const q = useQuery({
    queryKey: ADMIN_NAV_KEYS.attachments(caseId),
    queryFn: () => adminNavApi.caseAttachments(caseId),
  });

  if (q.isLoading) return <Loading />;
  if (q.isError) return <LoadError />;
  const rows: AdminNavAttachmentRow[] = q.data ?? [];

  return (
    <div className="rounded-[10px] border-[1.5px] border-nav-card bg-white p-3">
      <h3 className="font-head text-[12px] font-bold text-nav-navy mb-2">{t('navigator.docs.attachments')}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-nav-slate">{t('navigator.docs.noFiles')}</p>
      ) : (
        <ul className="text-sm flex flex-col gap-1.5">
          {rows.map((f) => (
            <li key={f.id} className="flex items-center justify-between gap-2">
              <span className="min-w-0 truncate text-nav-navy" title={f.filename}>
                {f.filename}
                <span className="text-[11px] text-nav-slate ml-2">
                  {fmtNy(f.created_at, 'date')}
                  {f.expired ? ` · ${t('navigator.docs.expiredBadge')}` : ''}
                </span>
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={!accessToken}
                onClick={() => {
                  if (!accessToken) return;
                  void downloadAdminAttachment(f.id, f.filename, accessToken)
                    .catch(() => toast.error(t('admin.nav.downloadFailed')));
                }}
              >
                <Download className="h-4 w-4" aria-hidden />
                {t('navigator.docs.download')}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** F5-R4a: exportado — lo reusa el «Abrir» del listado de Casos. */
export function CaseForms({ caseId }: { caseId: string }): JSX.Element {
  const t = useT();
  const q = useQuery({
    queryKey: ADMIN_NAV_KEYS.forms(caseId),
    queryFn: () => adminNavApi.caseForms(caseId),
  });
  if (q.isLoading) return <Loading />;
  if (q.isError) return <LoadError />;
  const rows: AdminNavFormRow[] = q.data ?? [];
  return (
    <div className="rounded-[10px] border-[1.5px] border-nav-card bg-white p-3">
      <h3 className="font-head text-[12px] font-bold text-nav-navy mb-2">{t('admin.nav.forms.title')}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-nav-slate">{t('admin.nav.forms.empty')}</p>
      ) : (
        <ul className="text-sm flex flex-col gap-1.5">
          {rows.map((f) => (
            <li key={f.id} className="flex justify-between gap-2">
              <span className="text-nav-navy">
                {t(FORM_TYPE_KEYS[f.formType] ?? 'navigator.docs.kind.other')}
                {f.exceptionReason ? ` · ${f.exceptionReason}` : ''}
              </span>
              <span className={cn(
                'text-[11px]',
                f.status === 'pending_evidence' ? 'text-nav-red font-medium' : 'text-nav-slate',
              )}>
                {/* D-11 — mismo dato que FormsLedger traduce: acá salía el enum
                    crudo ('pending_evidence') en los dos idiomas. Guard para
                    valores fuera del union: crudo antes que romper. */}
                {FORM_STATUS_KEYS[f.status as FormLedgerStatus]
                  ? t(FORM_STATUS_KEYS[f.status as FormLedgerStatus])
                  : f.status}
                {f.attachmentCount > 0 ? ` · ${f.attachmentCount}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ReassignForm({
  caseId, currentNavigatorId, loads,
}: {
  caseId: string;
  currentNavigatorId: string | null;
  loads: AdminNavLoad[];
}): JSX.Element {
  const t = useT();
  const qc = useQueryClient();
  const [navigatorId, setNavigatorId] = useState('');
  const [reason, setReason] = useState('');

  const mut = useMutation({
    mutationFn: () => adminNavApi.reassignMember(caseId, { navigatorId, reason }),
    onSuccess: (r) => {
      toast.success(
        t('admin.nav.reassignDone'),
        t('admin.nav.reassignDoneDetail', { n: r.cases, appts: r.futureAppointments }),
      );
      setReason('');
      setNavigatorId('');
      void qc.invalidateQueries({ queryKey: ['admin', 'navigation'] });
    },
    onError: (err) => toast.error(t('admin.nav.reassignFailed'), navError(err).message ?? ''),
  });

  const candidates = loads.filter((l) => l.navigatorId !== currentNavigatorId);

  return (
    <div className="rounded-[10px] border-[1.5px] border-nav-card bg-white p-3">
      <h3 className="font-head text-[12px] font-bold text-nav-navy mb-1">{t('admin.nav.reassign')}</h3>
      <p className="text-[11px] text-nav-slate mb-2">{t('admin.nav.reassignHint')}</p>
      <div className="flex flex-col gap-2">
        <div>
          <Label htmlFor={`reassign-nav-${caseId}`}>{t('admin.nav.reassignTo')}</Label>
          <Select
            id={`reassign-nav-${caseId}`}
            value={navigatorId}
            onChange={(e) => setNavigatorId(e.target.value)}
          >
            <option value="">{t('navigator.docs.choose')}</option>
            {candidates.map((l) => (
              <option key={l.navigatorId} value={l.navigatorId}>
                {l.navigatorName} ({l.score})
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label htmlFor={`reassign-reason-${caseId}`}>{t('admin.nav.reassignReason')}</Label>
          <Textarea
            id={`reassign-reason-${caseId}`}
            value={reason}
            maxLength={500}
            rows={2}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <Button
          variant="secondary"
          size="sm"
          disabled={!navigatorId || reason.trim().length < 3 || mut.isPending}
          onClick={() => mut.mutate()}
        >
          {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
          {t('admin.nav.reassign')}
        </Button>
      </div>
    </div>
  );
}

function Loading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex items-center gap-2 text-sm text-nav-slate py-6 px-4">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      {t('adminNav.loading')}
    </div>
  );
}

function LoadError(): JSX.Element {
  const t = useT();
  return (
    <Alert variant="error" role="alert">
      <AlertDescription>{t('adminNav.loadFailed')}</AlertDescription>
    </Alert>
  );
}
